/*
  Pure logic of the item page filters and of the files view. No business rules: the server decides what is
  required and what is written. These functions only choose what to highlight, fade or draw.
*/

import type { DeliverableFilter, FieldOnPage, FilesStatus, FilesView, Phase } from "../../../api/types";
import type { DisplayState } from "../../../ui";
import { STATE_WORDS } from "../../../ui";

/** Whether a field is one the chosen deliverable reads. The server names deliverables by key; the label is accepted too. */
export function isNeededBy(field: Pick<FieldOnPage, "needed_by_deliverables">, deliverable: DeliverableFilter | null): boolean {
  if (!deliverable) return false;
  return field.needed_by_deliverables.some((entry) => entry === deliverable.key || entry === deliverable.label);
}

/** Whether a field is required at the chosen phase (its lamp for that phase is lit). */
export function isRequiredAt(field: Pick<FieldOnPage, "phase">, phase: Phase | null): boolean {
  if (!phase) return true;
  return field.phase.lamps[phase] === true;
}

export type FieldLook = "normal" | "highlight" | "faded";

/**
 * How a field is drawn under the two filters: a deliverable highlights the fields it needs, and a phase fades the
 * fields that are not yet required. A faded field stays on the page.
 */
export function fieldLook(field: FieldOnPage, deliverable: DeliverableFilter | null, phase: Phase | null): FieldLook {
  if (phase && !isRequiredAt(field, phase)) return "faded";
  if (deliverable && isNeededBy(field, deliverable)) return "highlight";
  return "normal";
}

/** The words for the phase a field is required from. */
export function requiredFromWords(field: Pick<FieldOnPage, "phase">): string {
  const { required_from: from, per_case: perCase } = field.phase;
  if (perCase && Object.keys(perCase).length > 0) {
    return `Required from: ${Object.entries(perCase)
      .map(([name, phase]) => `${name} ${phase}`)
      .join(", ")}`;
  }
  return from ? `Required from ${from}` : "No phase set yet";
}

/** The kind of the pressure setting for a field that has one: "gauge" or "absolute". */
export function pressureWords(reference: string | null | undefined): string {
  if (reference === "gauge") return "gauge";
  if (reference === "absolute") return "absolute";
  return "";
}

/** The unit as written beside a value: "bar (gauge)". */
export function unitWords(unit: { symbol?: string | null; ucum_code?: string | null } | null | undefined, reference?: string | null): string {
  const base = unit?.symbol || unit?.ucum_code || "";
  const setting = pressureWords(reference);
  if (!base) return setting ? `(${setting})` : "";
  return setting ? `${base} (${setting})` : base;
}

// ---------------------------------------------------------------------------------------------- files view

export interface StateSegment {
  tone: DisplayState | "neutral";
  label: string;
  count: number;
  /** Share of the file's elements, 0 to 100. */
  share: number;
}

const TONE_OF_STATE: Record<string, DisplayState | "neutral"> = {
  validated: "validated",
  accepted: "waiting",
  added_by_hand: "waiting",
  waiting: "waiting",
  waiting_for_sme: "waiting",
  proposed: "proposed",
  sent_back: "sent_back",
  rejected: "rejected",
  loaded: "loaded",
  next_version: "waiting",
};

const ORDER: Array<DisplayState | "neutral"> = ["validated", "loaded", "waiting", "proposed", "sent_back", "rejected", "neutral"];

/** The bar of a file: its elements counted by state, in a fixed order, with their share of the whole. */
export function stateSegments(states: Record<string, number>): StateSegment[] {
  const byTone = new Map<DisplayState | "neutral", number>();
  let total = 0;
  for (const [state, count] of Object.entries(states)) {
    if (!(count > 0)) continue;
    const tone = TONE_OF_STATE[state] ?? "neutral";
    byTone.set(tone, (byTone.get(tone) ?? 0) + count);
    total += count;
  }
  return ORDER.filter((tone) => byTone.has(tone)).map((tone) => {
    const count = byTone.get(tone) ?? 0;
    return { tone, label: tone === "neutral" ? "Other" : STATE_WORDS[tone], count, share: total > 0 ? (count / total) * 100 : 0 };
  });
}

/** The status to show: the one the live channel pushed last, unless the list the page read is newer. */
export function pickFilesStatus(view: FilesView | undefined, live: FilesStatus | null): FilesStatus | null {
  const read = view?.status ?? null;
  if (!live) return read;
  if (!read) return live;
  if (live.status === "writing" || read.status === "writing") return live.status === "writing" ? live : read;
  return (live.event_rendered ?? -1) >= (read.event_rendered ?? -1) ? live : read;
}

/** The sentence under the files status, in plain words. */
export function filesStatusWords(status: FilesStatus | null | undefined): string {
  if (!status) return "";
  switch (status.status) {
    case "up_to_date":
      return status.event_rendered != null ? `Files up to date at event ${status.event_rendered}` : "Files up to date";
    case "writing":
      return "Writing the files now";
    case "check_failed":
      return "The files are not up to date: the last write failed its check";
    case "never_written":
      return "The files have not been written yet";
  }
}
